/**
 * Details step — split layout: Calendar (left 60%) | Form (right 40%).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import moment from 'moment-timezone';
import type { FormBuilderHandle, CalendarHandle } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import type { ScheduleStepProps } from '../../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../../config/modeClassifiers';
import { DetailsForm } from './DetailsForm';
import { useScheduleSharedState } from '../../state/useScheduleSharedState';
import { CalendarPanel } from '../../calendar/CalendarPanel';
import {
  CALENDAR_PREFETCH_WEEKS,
  CALENDAR_LOOKAHEAD_WEEKS,
} from '../../calendar/constants';
import { NoSlotsAvailablePopover } from '../../calendar/NoSlotsAvailablePopover';
import { useFormCalendarSync } from '../../calendar/useFormCalendarSync';
import {
  getMinimumNoticeHours,
  type MinimumNoticeSource,
} from '../../availability';
import { useAccountMinimumNotice } from '../../api/useAccountMinimumNotice';
import {
  SlotsPreviewModal,
  type FindTimeApplyArgs,
  type FindTimeValue,
  type SelfScheduleHandle,
  type SlotsPreviewData,
} from '../../calendar/selfSchedule';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  isHostOptionDisabled,
  type HostZoomStatus,
} from './fields/HostRenders';
import { useLocationFieldConfig } from '@/formConfigs';
import type { CurrentUserZoomStatus } from '../../initialValues/useWizardInitialValues';
import {
  pickAutoHostId,
  isExplicitlyInvalidHost,
} from './helpers/pickAutoHostId';
import { getCachedInterviewerOption } from '../../state/interviewerOptionCache';

interface DetailsStepProps extends ScheduleStepProps {
  interviewPlan?: Record<string, unknown>;
  /** Adhoc-only fallback for the current user's Zoom status. Non-adhoc
   *  flows read zoom status off `interviewPlan` directly; adhoc keeps
   *  `interviewPlan` undefined (so downstream behavioral branches stay
   *  off) and surfaces just this slice for the Host chip. */
  currentUserZoomStatus?: CurrentUserZoomStatus;
  /** Adhoc-only fallback for the plan-level zoom host strategy. Drives
   *  `pickAutoHostId` priority — non-adhoc reads `interviewPlan?.zoom_host_type`
   *  directly; adhoc surfaces just this slice from the plan response. */
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  candidateName?: string;
  candidateAvatar?: string;
}

export const DetailsStep = memo(
  forwardRef<StepFormApi, DetailsStepProps>(
    (
      {
        payloadRef,
        mode,
        apiReference,
        prefillVersion = 0,
        interviewPlan,
        currentUserZoomStatus,
        planZoomHostType: planZoomHostTypeProp,
        candidateName,
        candidateAvatar,
        candidatePhone,
        candidatePhoneValue,
        atsJobId,
        atsJobStageActionId,
        additionalSlotsContext,
        interviewPlatform,
      },
      ref
    ) => {
      const formRef = useRef<FormBuilderHandle>(null);
      const calendarRef = useRef<CalendarHandle>(null);
      const selfScheduleRef = useRef<SelfScheduleHandle>(null);
      const toast = useShowToast();
      const { state: sharedState, update: updateSharedState } =
        useScheduleSharedState();

      const isSelfSchedule = isSelfScheduleMode(mode);
      const isAdhoc = isAdhocMode(mode);
      const { feInterviewBuffer } = useKFlags() as {
        feInterviewBuffer?: boolean;
      };
      const isBufferEnabledByFlag =
        Boolean(feInterviewBuffer) && isSelfSchedule;

      // Resolve minimum-notice hours for the calendar + Find Time. Normal
      // self-schedule reads it off the plan response; adhoc / bulk have
      // no plan and need the account-level setting fetched directly.
      // Fetch only fires when the plan path produced nothing — normal
      // self-schedule never triggers this query.
      const planMinimumNoticeHours = getMinimumNoticeHours(
        interviewPlan as MinimumNoticeSource | undefined
      );
      const { data: accountMinimumNotice } = useAccountMinimumNotice({
        enabled: isSelfSchedule && planMinimumNoticeHours <= 0,
      });
      const minimumNoticeHours =
        planMinimumNoticeHours > 0
          ? planMinimumNoticeHours
          : getMinimumNoticeHours(accountMinimumNotice);

      const handleBufferChange = useCallback(
        (next: { before: number; after: number; enabled: boolean }) => {
          updateSharedState({
            bufferBeforeMinutes: next.before,
            bufferAfterMinutes: next.after,
            bufferEnabled: next.enabled,
          });
        },
        [updateSharedState]
      );

      // ─── Find Time state (self-schedule only) ──────────────────
      // `findTimeValue` is null when the user hasn't picked a preset
      // or has manually edited after applying one (dirty tracking).
      // The walker anchors on today and doesn't take an availability
      // oracle — it picks target dates unconditionally and the
      // pipeline handles what's actually renderable.
      const [findTimeValue, setFindTimeValue] = useState<FindTimeValue>(null);

      // Suppresses the dirty-reset the next time `onSlotsChange`
      // fires — we set it right before dispatching `applyFindTime`
      // so the flood of state updates that follows doesn't null out
      // the user's just-picked preset.
      const ignoreNextSlotEmitRef = useRef(false);

      const handleFindTimeApply = useCallback(
        ({ value, targetDateKeys, firstDateKey }: FindTimeApplyArgs) => {
          ignoreNextSlotEmitRef.current = true;
          selfScheduleRef.current?.applyFindTime({ targetDateKeys });
          setFindTimeValue(value);
          // Always navigate to the first target's week when Find
          // Time is applied. Covers both cases:
          //   - user clicks "Find time this week" from a different
          //     week → calendar scrolls back to today's week
          //   - user clicks "Next week" from current week →
          //     calendar scrolls forward to that week
          // The self-schedule calendar's `goToDate` imperatively
          // calls FullCalendar's `gotoDate`, which moves to the
          // week containing the given date.
          if (firstDateKey) {
            selfScheduleRef.current?.goToDate(firstDateKey);
          }
        },
        []
      );

      const handleFindTimeClear = useCallback(() => {
        ignoreNextSlotEmitRef.current = true;
        selfScheduleRef.current?.clearAll();
        setFindTimeValue(null);
      }, []);

      // Preview modal — opened from the FindTimeField's "Preview"
      // link, sends the current slot set + interview metadata into
      // the candidate-app iframe via postMessage (see
      // `SlotsPreviewModal`).
      const [isPreviewOpen, setPreviewOpen] = useState(false);
      const handlePreviewOpen = useCallback(() => setPreviewOpen(true), []);
      const handlePreviewClose = useCallback(() => setPreviewOpen(false), []);

      // Group slots by date for the preview iframe. Key must be derived
      // in the wizard timezone — `split('T')[0]` would land late-night
      // local slots under the prior UTC day.
      const selfScheduleSlotsForPreview = useMemo(() => {
        const result: Record<
          string,
          Array<{ start_time: string; end_time: string }>
        > = {};
        const tz = sharedState.timezone;
        for (const slot of sharedState.selfScheduleSlots ?? []) {
          const dateKey = moment.tz(slot.start, tz).format('YYYY-MM-DD');
          if (!result[dateKey]) result[dateKey] = [];
          result[dateKey].push({ start_time: slot.start, end_time: slot.end });
        }
        for (const dateKey of Object.keys(result)) {
          result[dateKey].sort((a, b) =>
            a.start_time.localeCompare(b.start_time)
          );
        }
        return result;
      }, [sharedState.selfScheduleSlots, sharedState.timezone]);

      // Multi-week availability flowing from the calendar's pipeline
      // — `YYYY-MM-DD` keys for every day with at least one bookable
      // slot. Drives the Find Time walker so picks land on real
      // availability instead of the hardcoded Mon-Fri set. `undefined`
      // until the first emit; walker falls back to legacy weekday
      // behaviour while it's missing.
      const [availableDays, setAvailableDays] = useState<
        ReadonlySet<string> | undefined
      >(undefined);
      const handleAvailableDaysChange = useCallback((keys: string[]) => {
        // Bail when the new keys match current content. Without this,
        // every emit creates a fresh Set whose reference inequality
        // re-renders DetailsStep → CalendarPanel → SelfScheduleCalendar,
        // and the calendar's `useEffect` re-emits the same keys, looping
        // forever and breaking FC mid-drag (resetOptions wipes the
        // in-flight drag's `validMutation` so `eventDrop` never fires).
        setAvailableDays((prev) => {
          if (
            prev &&
            prev.size === keys.length &&
            keys.every((k) => prev.has(k))
          ) {
            return prev;
          }
          return new Set(keys);
        });
      }, []);

      // Fresh key per Find-Time-no-availability event remounts the
      // popover so its internal "dismissed" state resets — without it
      // a user who dismissed once wouldn't see the next prompt.
      const [noAvailabilityKey, setNoAvailabilityKey] = useState<string | null>(
        null
      );
      const handleFindTimeNoAvailability = useCallback(
        (mode: FindTimeValue) => {
          setNoAvailabilityKey(`${mode ?? 'unknown'}-${Date.now()}`);
        },
        []
      );

      // Snapshot of the inputs the payload mapper needs to classify
      // each self-schedule slot's `is_custom` / `override_type` —
      // pushed up by `CalendarPanel.onScheduleContextChange` whenever
      // either side changes (gated on the panel side via content key
      // so it doesn't re-render us). Lives in a ref because we only
      // read it at save time inside `syncToPayload`; storing in state
      // would re-render the whole tree on every busy refetch for no
      // visual benefit.
      //
      // `isReady` starts `false` and flips `true` once the busy +
      // working-hours queries have settled. `validate()` blocks the
      // step transition while it's `false` so the user can't ship a
      // payload that classifies grey/busy slots as `'none'` purely
      // because the queries hadn't resolved yet (G1, send-additional-slots).
      const scheduleContextRef = useRef<{
        workingHoursByInterviewer: Record<
          string,
          Array<{ start: string; end: string }>
        >;
        busyEvents: ReadonlyArray<{
          id: string;
          start: string;
          end: string;
          interviewerId: string | number;
        }>;
        isReady: boolean;
      }>({
        workingHoursByInterviewer: {},
        busyEvents: [],
        isReady: false,
      });
      const handleScheduleContextChange = useCallback(
        (ctx: {
          workingHoursByInterviewer: Record<
            string,
            Array<{ start: string; end: string }>
          >;
          busyEvents: ReadonlyArray<{
            id: string;
            start: string;
            end: string;
            interviewerId: string | number;
          }>;
          isReady: boolean;
        }) => {
          scheduleContextRef.current = ctx;
        },
        []
      );

      // Edit-mode seed for the self-schedule calendar's reducer.
      // Read synchronously from `payloadRef` so `useReducer`'s lazy
      // init picks them up on first render — routing through shared
      // state alone would arrive a tick late and the reducer would
      // already be seeded empty.
      const initialSelfScheduleSlots = useMemo<
        | Array<{
            start: string;
            end: string;
            isBooked?: boolean;
            is_custom?: boolean;
            override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
          }>
        | undefined
      >(() => {
        if (apiReference !== 'edit' || !isSelfSchedule) return undefined;
        const slots = (payloadRef.current.details as Record<string, unknown>)
          .selfScheduleSlots as
          | Array<{
              start: string;
              end: string;
              is_custom?: boolean;
              override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
            }>
          | undefined;
        if (!Array.isArray(slots) || slots.length === 0) return undefined;
        const bookedStartMs = additionalSlotsContext
          ? Date.parse(additionalSlotsContext.bookedSlot.start)
          : Number.NaN;
        const bookedEndMs = additionalSlotsContext
          ? Date.parse(additionalSlotsContext.bookedSlot.end)
          : Number.NaN;
        // Forward `is_custom` / `override_type` from the hydrated
        // payload into the seed so the reducer can mark the seeded
        // pick `_fromServer` and the payload mapper preserves the
        // backend's classification on save (unless the user moves it).
        return slots.map((s) => {
          const isBooked =
            Number.isFinite(bookedStartMs) &&
            Number.isFinite(bookedEndMs) &&
            Date.parse(s.start) === bookedStartMs &&
            Date.parse(s.end) === bookedEndMs;
          return {
            start: s.start,
            end: s.end,
            ...(isBooked ? { isBooked: true } : {}),
            ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
            ...(s.override_type ? { override_type: s.override_type } : {}),
          };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        apiReference,
        isSelfSchedule,
        payloadRef,
        prefillVersion,
        additionalSlotsContext,
      ]);

      // Seed `findTimeValue` from the hydrated preset once on mount.
      const hydratedFindTimeRef = useRef(false);
      useEffect(() => {
        if (hydratedFindTimeRef.current) return;
        if (apiReference !== 'edit' || !isSelfSchedule) return;
        const ftv = (payloadRef.current.details as Record<string, unknown>)
          .findTimeValue as FindTimeValue | undefined;
        if (ftv) {
          hydratedFindTimeRef.current = true;
          setFindTimeValue(ftv);
        }
      }, [apiReference, isSelfSchedule, payloadRef, prefillVersion]);

      const { handleFieldChange } = useFormCalendarSync({
        formRef,
        sharedState,
        updateSharedState,
        calendarRef,
      });

      // Seed duration from the interview plan so edit mode + presets
      // reach the calendar overlay before the user touches the form.
      useEffect(() => {
        const planDuration =
          typeof interviewPlan?.duration === 'number' &&
          interviewPlan.duration > 0
            ? interviewPlan.duration
            : 30;
        if (planDuration !== sharedState.duration) {
          updateSharedState({ duration: planDuration });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [interviewPlan?.duration]);

      // Edit: seed sharedState from the hydrated payloadRef. Create
      // (with an interview plan): seed the same fields from the plan
      // so the calendar fires its busy-events fetch for the plan's
      // default interviewers — otherwise sharedState stays empty
      // until the user manually changes a field, and the calendar
      // shows a blank week even though the form already has the
      // interviewers prefilled.
      useEffect(() => {
        const updates: Partial<typeof sharedState> = {};
        if (apiReference === 'edit') {
          const details = payloadRef.current.details as Record<string, unknown>;
          const bs = details.bookedSlot as
            | { start?: string; end?: string; duration?: number }
            | undefined;
          if (bs?.start && bs?.end) {
            updates.bookedSlot = {
              start: bs.start,
              end: bs.end,
              duration: bs.duration ?? sharedState.duration,
            };
          }
          // Mirror the hydrated duration into top-level `sharedState.duration`
          // — the calendar's click-to-move handler reads it as the slot
          // length when the user clicks a 15-min cell to move the booked
          // event (`useCalendarEventHandlers.handleSelect` builds
          // `end = start + duration`). The plan-duration seed effect above
          // can't pick this up because GET `/ats_interviews/:id` does NOT
          // return a top-level `duration` field — it's computed from
          // `(end_time - start_time)` inside `hydrateDetailsFromApi` and
          // landed on `payloadRef.details.duration`. Without this mirror,
          // sharedState.duration stays at the default 30 and a 2-min
          // interview silently becomes 30 mins on first click-to-move.
          const detailsDuration = details.duration;
          if (
            typeof detailsDuration === 'number' &&
            detailsDuration > 0 &&
            detailsDuration !== sharedState.duration
          ) {
            updates.duration = detailsDuration;
          }
          // Same pattern for `interviewKind` — sharedState defaults to
          // `'one_on_one'` and the calendar's availability algorithm uses
          // it to pick union (one_on_one) vs intersection (panel) of
          // interviewers' free time. Without this seed, editing a panel
          // interview shows union availability until the user touches
          // the interview type field. `useFormCalendarSync` already
          // syncs subsequent user changes via the `interview_type` case.
          const detailsKind = details.interview_type;
          if (
            (detailsKind === 'panel' || detailsKind === 'one_on_one') &&
            detailsKind !== sharedState.interviewKind
          ) {
            updates.interviewKind = detailsKind;
          }
          // Self-schedule edit: seed shared state with the hydrated
          // time slots so the calendar's reducer sees them on first
          // paint. Without this the pre-selected slots silently fall
          // away — payloadRef has them, but the form-side sync only
          // picked up `bookedSlot` before.
          const slots = details.selfScheduleSlots as
            | Array<{
                start: string;
                end: string;
                duration?: number;
                is_custom?: boolean;
                override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
              }>
            | undefined;
          if (
            isSelfSchedule &&
            Array.isArray(slots) &&
            slots.length > 0 &&
            (sharedState.selfScheduleSlots?.length ?? 0) === 0
          ) {
            // Mark hydrated slots as `_fromServer: true` so the payload
            // mapper preserves their override classification verbatim
            // until the user moves them. Stripping this would mean
            // every save recomputes — fine in theory, but legacy
            // preserves server values to avoid drift if the backend
            // classification logic differs from ours (it does — backend
            // has account-level working-hours overrides we don't model
            // here).
            updates.selfScheduleSlots = slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: s.duration ?? sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              _fromServer: true,
            }));
          }
          const bb = details.bufferBeforeMinutes;
          const ba = details.bufferAfterMinutes;
          if (typeof bb === 'number') updates.bufferBeforeMinutes = bb;
          if (typeof ba === 'number') updates.bufferAfterMinutes = ba;
          const ids = details.participant_ids;
          if (Array.isArray(ids)) {
            updates.interviewerIds = ids.map(Number).filter(Number.isFinite);
          } else if (typeof ids === 'string' && ids) {
            const n = Number(ids);
            if (Number.isFinite(n)) updates.interviewerIds = [n];
          }
          const rooms = details.meeting_room_ids;
          if (Array.isArray(rooms)) {
            updates.meetingRoomEmails = rooms.map(String).filter(Boolean);
          } else if (typeof rooms === 'string' && rooms) {
            updates.meetingRoomEmails = [rooms];
          }
          const tz = details.timezone;
          if (typeof tz === 'string' && tz) updates.timezone = tz;
          const name = details.interviewName;
          if (typeof name === 'string' && name) {
            updates.interviewName = name;
            updates.originalInterviewName = name;
          }
        } else if (interviewPlan) {
          // Create with plan: read from payloadRef.details (already mapped
          // to form field names by hydratePlanDefaults) — same field names
          // as the edit branch above so guards and reads are consistent.
          const planDetails = payloadRef.current.details as Record<
            string,
            unknown
          >;
          const rawIds = planDetails.participant_ids;
          const allIds = (
            Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : []
          )
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n) && n > 0);

          // For a one-on-one (manual mode), the plan's participants list
          // is a POOL — only ONE attends. Limit the calendar feed to the
          // first so it doesn't show busy blocks for all pool members.
          // Panel + self-schedule send everyone (all attend).
          const planKind = planDetails.interview_type as string | undefined;
          const useAllIds = planKind === 'panel' || isSelfSchedule;
          const idsForCalendar = useAllIds ? allIds : allIds.slice(0, 1);

          if (
            idsForCalendar.length > 0 &&
            sharedState.interviewerIds.length === 0
          ) {
            updates.interviewerIds = idsForCalendar;
          }

          const rawPlanRooms = planDetails.meeting_room_ids;
          const planRooms: string[] = Array.isArray(rawPlanRooms)
            ? (rawPlanRooms as string[]).filter(Boolean)
            : typeof rawPlanRooms === 'string' && rawPlanRooms
              ? [rawPlanRooms]
              : [];
          if (
            planRooms.length > 0 &&
            sharedState.meetingRoomEmails.length === 0
          ) {
            updates.meetingRoomEmails = planRooms;
          }

          const planName = planDetails.interviewName as string | undefined;
          if (
            typeof planName === 'string' &&
            planName &&
            !sharedState.interviewName
          ) {
            updates.interviewName = planName;
          }

          if (
            (planKind === 'panel' || planKind === 'one_on_one') &&
            sharedState.interviewKind !== planKind
          ) {
            updates.interviewKind = planKind;
          }
        }
        if (Object.keys(updates).length > 0) {
          updateSharedState(updates);
        }
        // Only seed on (re)mount keyed by prefillVersion (edit) or
        // when the plan becomes available (create). `updateSharedState`
        // is stable; depending on `sharedState` would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [apiReference, prefillVersion, interviewPlan]);

      const handleVisibleWeekStartChange = useCallback(
        (iso: string) => {
          updateSharedState({ calendarWeekStart: iso });
        },
        [updateSharedState]
      );

      const handleCalendarAvailabilityChange = useCallback(
        (disconnectedIds: number[]) => {
          updateSharedState({
            calendarDisconnectedInterviewerIds: disconnectedIds,
          });
        },
        [updateSharedState]
      );

      const handleSlotSelect = useCallback(
        (slot: { start: string; end: string }) => {
          // Manual-only path (self-schedule's slot collection flows
          // through `handleSlotsChange`). The dragged-slot bounds are
          // authoritative — derive duration from them so a drag-to-
          // resize on the calendar reflects on the form's Duration
          // field. Click selection keeps the existing duration
          // because `useCalendarEventHandlers.handleSelect` already
          // builds `end = start + duration` for clicks; only drags
          // produce a different span.
          const startMs = new Date(slot.start).getTime();
          const endMs = new Date(slot.end).getTime();
          const slotDuration = Math.max(
            1,
            Math.round((endMs - startMs) / 60000)
          );
          updateSharedState({
            bookedSlot: {
              start: slot.start,
              end: slot.end,
              duration: slotDuration,
            },
            duration: slotDuration,
          });
        },
        [updateSharedState]
      );

      // ── Host (Zoom) field props ──────────────────────────────────────────
      // The Host Select renders only the picked interviewers + the current
      // user (legacy `SchZoomHost` constraint). zoom_status comes from the
      // create/edit API responses (`user_zoom_status` block + each
      // participant's zoom_status partial); for adhoc create with no plan
      // we leave it undefined and the chip skips the tag.
      const currUser = useSelector(selectCurrUser) as
        | {
            id?: number | string;
            first_name?: string;
            last_name?: string;
            avatar_url?: string;
            avatar?: string;
          }
        | null
        | undefined;

      // The current user's zoom status comes back in two different shapes
      // depending on the endpoint:
      //   - GET /ats_interviews/new (create-from-plan): top-level FLAT
      //     `can_host_zoom_meeting`, `zoom_rate_limit_reached`, `zoom_plan`,
      //     `zoom_plan_type` (rendered straight by `_zoom_status` partial
      //     at the root of the response).
      //   - GET /ats_interviews/:id (edit): NESTED under `user_zoom_status`
      //     for the original interview creator.
      // Memoised so the resulting reference is stable across renders that
      // don't actually change zoom-status values. Without this the
      // `currentUserHostInfo` useMemo depends on a fresh object every
      // render and the auto-pick effect (and any consumer that diffs on
      // its identity) re-fires unnecessarily.
      const userZoomStatus = useMemo<
        (HostZoomStatus & { id?: number | string }) | null
      >(() => {
        const p = interviewPlan as Record<string, unknown> | undefined;
        const nested = (p?.user_zoom_status ?? null) as
          | (HostZoomStatus & { id?: number | string })
          | null;
        if (nested) return nested;
        if (!p) {
          // Adhoc: `interviewPlan` is intentionally undefined so plan-
          // derived behavioral branches stay off. Zoom status arrives
          // separately as `currentUserZoomStatus` (pulled from the same
          // plan response in `useWizardInitialValues`). No `id` here —
          // the value represents the current user by construction, and
          // the `id`-match guard in `currentUserHostInfo` accepts
          // `id === undefined`.
          return currentUserZoomStatus ?? null;
        }
        if (
          'can_host_zoom_meeting' in p ||
          'zoom_plan' in p ||
          'zoom_rate_limit_reached' in p
        ) {
          return {
            can_host_zoom_meeting: p.can_host_zoom_meeting as
              | boolean
              | undefined,
            zoom_rate_limit_reached: p.zoom_rate_limit_reached as
              | boolean
              | undefined,
            zoom_plan: p.zoom_plan as number | undefined,
            zoom_plan_type: p.zoom_plan_type as string | undefined,
          };
        }
        return null;
        // Intentional narrow deps: only the zoom fields we read here.
        // Listing the full `interviewPlan` would invalidate this memo
        // every time any unrelated key on the response changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        interviewPlan?.user_zoom_status,
        interviewPlan?.can_host_zoom_meeting,
        interviewPlan?.zoom_plan,
        interviewPlan?.zoom_plan_type,
        interviewPlan?.zoom_rate_limit_reached,
        currentUserZoomStatus,
      ]);

      const currentUserHostInfo = useMemo(() => {
        if (!currUser?.id) return undefined;
        const fullName =
          `${currUser.first_name ?? ''} ${currUser.last_name ?? ''}`.trim() ||
          `User ${currUser.id}`;
        return {
          id: currUser.id,
          label: fullName,
          image: currUser.avatar_url ?? currUser.avatar,
          // user_zoom_status is keyed against the original interview
          // creator in edit mode; only apply it when its id matches the
          // current user's id, otherwise leave undefined (the chip skips
          // the tag rather than rendering a misleading status).
          zoomStatus:
            userZoomStatus &&
            (userZoomStatus.id === undefined ||
              String(userZoomStatus.id) === String(currUser.id))
              ? {
                  can_host_zoom_meeting: userZoomStatus.can_host_zoom_meeting,
                  zoom_rate_limit_reached:
                    userZoomStatus.zoom_rate_limit_reached,
                  zoom_plan: userZoomStatus.zoom_plan,
                  zoom_plan_type: userZoomStatus.zoom_plan_type,
                }
              : undefined,
        };
      }, [
        currUser?.id,
        currUser?.first_name,
        currUser?.last_name,
        currUser?.avatar_url,
        currUser?.avatar,
        userZoomStatus,
      ]);

      // Build the actual participant options for the Host field — only
      // currently-selected interviewers, NOT the full plan pool.
      // `sharedState.interviewerIds` is the source of truth; we resolve
      // each id against (a) `participant_default_options` (the chip
      // hydration source on edit / pool flows) and (b) the
      // `interviewerOptionCache` (populated as the user searches the
      // async-select). This matches legacy `SchZoomHost`'s reliance on
      // `interviewersDetails` (= picked interviewers) instead of the
      // wider plan pool.
      const selectedHostParticipantOptions = useMemo(() => {
        const defaults = (payloadRef.current.details
          ?.participant_default_options ?? []) as Array<{
          value: string;
          label: string;
          image?: string;
          zoomStatus?: HostZoomStatus;
        }>;
        const defaultsById = new Map(
          defaults.map((d) => [String(d.value), d] as const)
        );
        return sharedState.interviewerIds
          .map((id) => {
            const key = String(id);
            const fromDefaults = defaultsById.get(key);
            if (fromDefaults) return fromDefaults;
            const fromCache = getCachedInterviewerOption(key);
            if (fromCache) {
              return {
                value: key,
                label: fromCache.label,
                image: fromCache.image,
                zoomStatus: fromCache.zoomStatus,
              };
            }
            return null;
          })
          .filter(
            (
              o
            ): o is {
              value: string;
              label: string;
              image?: string;
              zoomStatus?: HostZoomStatus;
            } => o !== null
          );
      }, [sharedState.interviewerIds, payloadRef]);

      // In edit mode, the existing interview's `host` block carries the
      // saved host with their current zoom status. Surface as a Select
      // option so the chip still renders even when the interviewer list
      // has been swapped since the original schedule.
      const editSavedHost = useMemo(() => {
        if (apiReference !== 'edit') return undefined;
        const host = interviewPlan?.host as
          | (HostZoomStatus & {
              id?: number | string;
              name?: string;
              avatar?: string;
              avatar_url?: string;
            })
          | null
          | undefined;
        if (!host?.id) return undefined;
        return {
          value: String(host.id),
          label: host.name ?? `User ${host.id}`,
          image: host.avatar_url ?? host.avatar,
          zoomStatus: {
            can_host_zoom_meeting: host.can_host_zoom_meeting,
            zoom_rate_limit_reached: host.zoom_rate_limit_reached,
            zoom_plan: host.zoom_plan,
            zoom_plan_type: host.zoom_plan_type,
          },
        };
      }, [apiReference, interviewPlan?.host]);

      // ── Host auto-selection ─────────────────────────────────────────────
      // Pre-fills the Host (Zoom) field. Strategy:
      //   1. If `host_id` is already set (BE pre-fill on plan, or edit
      //      hydration) AND the user behind it is still a valid Zoom host,
      //      keep it as-is.
      //   2. If `host_id` is set but the user is EXPLICITLY invalid (BE
      //      returned `can_host_zoom_meeting: false`, rate-limited, or
      //      unlicensed), clear it and re-pick from `pickAutoHostId`. This
      //      handles the "user lost Zoom access since plan was created"
      //      edge case the user flagged.
      //   3. If `host_id` is empty, run `pickAutoHostId` and use its result
      //      (which respects the plan's `zoom_host_type` to decide order).
      //
      // Reactivity: depends on `selectedHostParticipantOptions` so the
      // effect re-runs whenever the picked-interviewer list (with their
      // zoom_status) changes — e.g. roster additions / removals — rather
      // than only when the id-array changes. Stable refs across renders
      // keep this from looping.
      // Non-adhoc reads zoom_host_type off the raw plan response;
      // adhoc keeps `interviewPlan` undefined for downstream branching,
      // so the same slice arrives as a separate prop (legacy parity:
      // legacy adhoc always had this field on its plan response and
      // pickAutoHostId honored it).
      const planHostType =
        (interviewPlan?.zoom_host_type as
          | 'organizer'
          | 'interviewer'
          | string
          | null
          | undefined) ?? planZoomHostTypeProp;
      useEffect(() => {
        const formApi = formRef.current;
        if (!formApi) return;
        const values = formApi.getValues() as Record<string, unknown>;
        if (values.location !== 'zoom') return;

        // Build the unified candidate list (interviewers + current user)
        // so we can validate any existing `host_id` against it.
        const candidates: Array<{
          value: string;
          zoomStatus?: HostZoomStatus;
        }> = [...selectedHostParticipantOptions];
        if (currentUserHostInfo) {
          candidates.push({
            value: String(currentUserHostInfo.id),
            zoomStatus: currentUserHostInfo.zoomStatus,
          });
        }

        const currentHostId = (values.host_id ?? '') as string;
        if (currentHostId) {
          const existing = candidates.find((c) => c.value === currentHostId);
          // Keep existing as long as it's not EXPLICITLY invalid. We only
          // override when the BE has flagged the user can't host — never
          // when zoom status is simply unknown / not loaded yet (would
          // strip valid edit-hydrated values during loading).
          if (!existing || !isExplicitlyInvalidHost(existing.zoomStatus)) {
            return;
          }
        }

        const picked = pickAutoHostId({
          participantOptions: selectedHostParticipantOptions,
          currentUser: currentUserHostInfo
            ? {
                id: currentUserHostInfo.id,
                zoomStatus: currentUserHostInfo.zoomStatus,
              }
            : undefined,
          planHostType,
        });
        const next = picked ?? '';
        if (next !== currentHostId) {
          formApi.setValue('host_id', next);
        }
      }, [selectedHostParticipantOptions, currentUserHostInfo, planHostType]);

      // ── Zoom location auto-fallback ─────────────────────────────────────
      // When every candidate host (picked interviewers + recruiter + edit-
      // saved host) is "Not part of Zoom" / rate-limited, the Zoom option
      // is disabled in the Location dropdown. If the form already has
      // `location: 'zoom'` (default value or prior valid state), switch it
      // to the next enabled option so the recruiter isn't stranded on an
      // empty Host dropdown.
      const zoomHasNoEligibleHost = useMemo(() => {
        const candidates = [
          ...selectedHostParticipantOptions,
          ...(currentUserHostInfo ? [currentUserHostInfo] : []),
          ...(editSavedHost ? [editSavedHost] : []),
        ];
        if (candidates.length === 0) return false;
        return candidates.every((c) => isHostOptionDisabled(c.zoomStatus));
      }, [selectedHostParticipantOptions, currentUserHostInfo, editSavedHost]);
      const { defaultLocation: fallbackLocation } = useLocationFieldConfig({
        zoomHasNoEligibleHost,
      });
      useEffect(() => {
        if (!zoomHasNoEligibleHost) return;
        const formApi = formRef.current;
        if (!formApi) return;
        const values = formApi.getValues() as Record<string, unknown>;
        if (values.location !== 'zoom') return;
        if (!fallbackLocation || fallbackLocation === 'zoom') return;
        formApi.setValue('location', fallbackLocation);
      }, [zoomHasNoEligibleHost, fallbackLocation]);

      // Stable callback so DetailsForm's effect doesn't re-fire every
      // render and trigger the shared-store to re-notify listeners in
      // a loop.
      const handleMeetingRoomNamesChange = useCallback(
        (namesByEmail: Record<string, string>) => {
          updateSharedState({ meetingRoomNamesByEmail: namesByEmail });
        },
        [updateSharedState]
      );

      const handleSlotsChange = useCallback(
        (
          slots: Array<{
            start: string;
            end: string;
            is_custom?: boolean;
            override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
            _fromServer?: boolean;
          }>
        ) => {
          updateSharedState({
            // Preserve override flags + `_fromServer` gate from the
            // calendar emit. `mapDetails` reads these at save time and
            // either passes them through (server-sourced + unmoved) or
            // recomputes via `computeSlotOverride`. Stripping them here
            // would silently send `is_custom: false` for legitimate
            // override picks, making them unbookable by candidates
            // (backend's working-hours / busy-event filters reject the
            // booking attempt). Same parity gap that motivated this
            // whole pass.
            selfScheduleSlots: slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              ...(s._fromServer ? { _fromServer: true } : {}),
            })),
          });
          // Dirty-track the Find Time selection. Any emit that isn't
          // the one triggered by our own `applyFindTime`/`clearAll`
          // call (those set `ignoreNextSlotEmitRef = true`) is a
          // manual edit and should null out the active preset.
          if (ignoreNextSlotEmitRef.current) {
            ignoreNextSlotEmitRef.current = false;
          } else if (findTimeValue !== null) {
            setFindTimeValue(null);
          }
        },
        [findTimeValue, sharedState.duration, updateSharedState]
      );

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!formRef.current) return true;
            const slot = sharedState.bookedSlot;
            const needsSlot = !isSelfSchedule && (!slot?.start || !slot?.end);
            // Self-schedule's analogue: at least one picked slot
            // before moving on. Mirrors manual's toast so the user
            // gets the same failure signal on both modes.
            const needsSelfSlots =
              isSelfSchedule &&
              (sharedState.selfScheduleSlots?.length ?? 0) === 0;
            // Self-schedule guard against the empty-snapshot race
            // (G1, send-additional-slots): if the user clicks Save/Next
            // before CalendarPanel's busy + working-hours queries have
            // settled, every newly drawn slot would classify as
            // `override_type: 'none'` regardless of where it sits on
            // the grid — making backend filter rejections invisible to
            // the recruiter. Block the transition with a toast so they
            // can retry once the data lands. Skip the block when every
            // slot is `_fromServer: true` (edit-mode hydration where
            // the user touched nothing else on the calendar) — those
            // pass through verbatim in `mapDetails` and don't need a
            // fresh classifier.
            const hasUnclassifiedSlot =
              isSelfSchedule &&
              (sharedState.selfScheduleSlots ?? []).some((s) => !s._fromServer);
            const needsScheduleContext =
              isSelfSchedule &&
              !needsSelfSlots &&
              hasUnclassifiedSlot &&
              !scheduleContextRef.current.isReady;
            // Self-schedule mandates a timezone for every picked
            // interviewer — the candidate-facing app reads each
            // interviewer's timezone when rendering their availability,
            // and a blank value collapses to UTC which silently shifts
            // every slot off the recruiter's intended grid. Legacy
            // opened a fix-it modal here (`SchInterviewerTimezoneModal`);
            // the new wizard already exposes the same modal inline via
            // the globe icon on each chip, so the validator just blocks
            // with a toast pointing the recruiter at the affected name.
            const missingTzNames: string[] = (() => {
              if (!isSelfSchedule) return [];
              const ids = sharedState.interviewerIds ?? [];
              if (ids.length === 0) return [];
              const opts = (
                payloadRef.current.details as Record<string, unknown>
              )?.participant_default_options as
                | Array<{
                    value: string;
                    label: string;
                    data?: { timezone?: string };
                  }>
                | undefined;
              const out: string[] = [];
              for (const rawId of ids) {
                const id = String(rawId);
                const seed = Array.isArray(opts)
                  ? opts.find((o) => String(o.value) === id)
                  : undefined;
                const cached = getCachedInterviewerOption(id);
                const tz =
                  (seed?.data?.timezone && seed.data.timezone.trim()) ||
                  (cached?.timezone && cached.timezone.trim()) ||
                  '';
                if (!tz) {
                  out.push(seed?.label || cached?.label || `User ${id}`);
                }
              }
              return out;
            })();
            const needsTimezone = missingTzNames.length > 0;
            if (needsSlot) {
              toast({
                id: 'schedule-interview-no-slot',
                title: 'Pick a time slot on the calendar',
                status: 'error',
              });
            } else if (needsSelfSlots) {
              toast({
                id: 'schedule-interview-no-self-slots',
                title: 'Pick at least one slot on the calendar',
                status: 'error',
              });
            } else if (needsTimezone) {
              toast({
                id: 'schedule-interview-missing-tz',
                title:
                  missingTzNames.length === 1
                    ? `Set timezone for ${missingTzNames[0]} so candidates can see their availability`
                    : 'Set timezones for the picked interviewers so candidates can see their availability',
                status: 'error',
              });
            } else if (needsScheduleContext) {
              toast({
                id: 'schedule-interview-context-loading',
                title: 'Loading availability — please try again in a moment',
                status: 'error',
              });
            }
            const isFormValid = await formRef.current.trigger();
            return (
              isFormValid &&
              !needsSlot &&
              !needsSelfSlots &&
              !needsTimezone &&
              !needsScheduleContext
            );
          },
          syncToPayload: () => {
            if (!formRef.current) return;
            const formValues = formRef.current.getValues();
            const shouldEmitBuffer =
              isBufferEnabledByFlag && sharedState.bufferEnabled;
            // Edit-mode `interviewPlan === existingInterview` (the raw
            // GET response), so its `meeting_rooms` is the
            // server-current room list. mapDetails uses it to diff
            // against `meeting_room_ids` and emit `_destroy: true` for
            // removed entries — the parity gap audit caught this gap
            // (legacy did the diff; the new wizard was only emitting
            // additions, so removed rooms stayed attached server-side).
            // Create mode → no `meeting_rooms` on the plan → mapDetails
            // skips deletion logic and behaves as before.
            const originalMeetingRooms = Array.isArray(
              (interviewPlan as { meeting_rooms?: unknown })?.meeting_rooms
            )
              ? ((interviewPlan as { meeting_rooms: unknown[] })
                  .meeting_rooms as Array<{
                  id?: number | string;
                  email?: string;
                  name?: string;
                }>)
              : [];
            payloadRef.current.details = {
              ...formValues,
              bookedSlot: sharedState.bookedSlot,
              selfScheduleSlots: sharedState.selfScheduleSlots,
              timezone: sharedState.timezone,
              // Adhoc has no plan to opt-in from. Mirror the schema
              // gate in `useScheduleCalendarData` — picking a calendar
              // in adhoc IS the opt-in, so mapDetails should ship
              // `calendar_info` to the BE.
              shared_calendar_enabled:
                isAdhoc || Boolean(interviewPlan?.shared_calendar_enabled),
              // Round-tripped into `schedule_type` by the payload
              // builder (`scheduleTypeFor`). `null` → 'Custom'.
              findTimeValue,
              // Plan-level meeting-rooms toggle — drives the "feature
              // disabled → wipe all rooms" branch in mapDetails.
              meeting_rooms_enabled:
                (interviewPlan as { meeting_rooms_enabled?: unknown })
                  ?.meeting_rooms_enabled === undefined
                  ? true
                  : Boolean(
                      (interviewPlan as { meeting_rooms_enabled?: unknown })
                        .meeting_rooms_enabled
                    ),
              // Snapshot of server-side rooms — UI-only key consumed by
              // mapDetails for the deletion diff. Stripped before wire
              // because mapDetails builds `mapped` key-by-key.
              _originalMeetingRooms: originalMeetingRooms,
              // Email → display-name lookup so mapDetails can stamp the
              // real room name onto `meeting_rooms_attributes[].name`
              // when adding a new room. Without this the mapper falls
              // back to using the email as the name, which the backend
              // stores verbatim and shows in upcoming-interview lists.
              _meetingRoomNamesByEmail: sharedState.meetingRoomNamesByEmail,
              // Self-schedule only — snapshot the override-classifier
              // inputs into the details payload so `mapDetails` can
              // compute `is_custom` / `override_type` per slot at save
              // time. `_overrideContext` is a UI-only key; the payload
              // builder reads + strips it before serialising. See
              // `payload/computeSlotOverride.ts` for the algorithm.
              //
              // Only emit when the snapshot is ready (busy + WH queries
              // have settled). When not ready, `validate()` above has
              // already blocked the transition with a toast, so this is
              // belt-and-suspenders: even if a caller bypassed validate,
              // mapDetails falls through to the slot's stored fields
              // instead of computing against empty data.
              ...(isSelfSchedule && scheduleContextRef.current.isReady
                ? {
                    _overrideContext: {
                      workingHoursByInterviewer:
                        scheduleContextRef.current.workingHoursByInterviewer,
                      busyEvents: scheduleContextRef.current.busyEvents,
                      interviewerIds: sharedState.interviewerIds,
                      interviewKind: sharedState.interviewKind,
                    },
                  }
                : {}),
              ...(shouldEmitBuffer
                ? {
                    bufferBeforeMinutes: sharedState.bufferBeforeMinutes,
                    bufferAfterMinutes: sharedState.bufferAfterMinutes,
                  }
                : {}),
            };
          },
        }),
        [
          payloadRef,
          sharedState.bookedSlot,
          sharedState.selfScheduleSlots,
          sharedState.timezone,
          sharedState.bufferEnabled,
          sharedState.bufferBeforeMinutes,
          sharedState.bufferAfterMinutes,
          sharedState.interviewerIds,
          sharedState.interviewKind,
          sharedState.meetingRoomNamesByEmail,
          interviewPlan,
          isSelfSchedule,
          isAdhoc,
          isBufferEnabledByFlag,
          toast,
          findTimeValue,
        ]
      );

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="0"
        >
          {/* Left panel — Calendar */}
          <Box
            position="relative"
            flex="3"
            minW={0}
            h="100%"
            borderRight="1px solid"
            borderColor="border-color"
            overflow="hidden"
          >
            <CalendarPanel
              ref={calendarRef}
              mode={mode}
              interviewKind={sharedState.interviewKind}
              prefetchWeeks={CALENDAR_PREFETCH_WEEKS}
              lookaheadPrefetchWeeks={CALENDAR_LOOKAHEAD_WEEKS}
              duration={sharedState.duration}
              timezone={sharedState.timezone}
              interviewerIds={sharedState.interviewerIds.map(String)}
              meetingRoomIds={sharedState.meetingRoomIds.map(String)}
              meetingRoomEmails={sharedState.meetingRoomEmails}
              meetingRoomNamesByEmail={sharedState.meetingRoomNamesByEmail}
              interviewName={sharedState.interviewName}
              originalInterviewName={sharedState.originalInterviewName}
              candidateName={candidateName}
              bookedSlot={sharedState.bookedSlot}
              onSlotSelect={handleSlotSelect}
              onTimezoneChange={(tz) => updateSharedState({ timezone: tz })}
              onSlotsChange={handleSlotsChange}
              onVisibleWeekStartChange={handleVisibleWeekStartChange}
              onCalendarAvailabilityChange={handleCalendarAvailabilityChange}
              onAvailableDaysChange={handleAvailableDaysChange}
              onScheduleContextChange={handleScheduleContextChange}
              isEdit={apiReference === 'edit'}
              atsJobId={atsJobId}
              minimumNoticeHours={minimumNoticeHours}
              selfScheduleRef={selfScheduleRef}
              initialSelfScheduleSlots={initialSelfScheduleSlots}
              bufferBeforeMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferBeforeMinutes
                  : 0
              }
              bufferAfterMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferAfterMinutes
                  : 0
              }
            />
            {/* Find-Time "selected interviewers not available" popover.
                 Mounted here so the absolute-positioned popover overlays
                 the calendar grid (same visual home as the existing
                 "no slots this week" variant). Each fire sets a fresh
                 key, remounting the dismissable popover. */}
            {noAvailabilityKey && (
              <NoSlotsAvailablePopover
                key={noAvailabilityKey}
                visible
                reason="find-time"
              />
            )}
          </Box>

          {/* Right panel — Form */}
          <Box
            w="400px"
            maxW="400px"
            minW="400px"
            flexShrink={0}
            h="100%"
            overflowY="auto"
            px="5"
            py="4"
          >
            <DetailsForm
              ref={formRef}
              mode={mode}
              apiReference={apiReference}
              payloadRef={payloadRef}
              interviewPlan={interviewPlan}
              interviewPlatform={interviewPlatform}
              calendarBookedStartIso={sharedState.bookedSlot?.start ?? null}
              calendarBookedEndIso={sharedState.bookedSlot?.end ?? null}
              visibleWeekStartIso={sharedState.calendarWeekStart || null}
              disconnectedInterviewerIds={
                sharedState.calendarDisconnectedInterviewerIds
              }
              scheduleTimezone={sharedState.timezone}
              candidateName={candidateName}
              candidateAvatar={candidateAvatar}
              candidatePhone={candidatePhone}
              candidatePhoneValue={candidatePhoneValue}
              atsJobStageActionId={atsJobStageActionId}
              // Stops the AI-recommended-interviewer query as soon as
              // the user picks anyone — `useFormCalendarSync` mirrors
              // `participant_ids` into `sharedState.interviewerIds` on
              // every form change, so this flips before the next
              // render of the schema. Without it, dragging the booked
              // slot kept refetching the stale recommendation.
              hasInterviewerSelected={sharedState.interviewerIds.length > 0}
              hostParticipantOptions={selectedHostParticipantOptions}
              currentUserHostInfo={currentUserHostInfo}
              editSavedHost={editSavedHost}
              onFieldChange={handleFieldChange}
              onMeetingRoomNamesChange={handleMeetingRoomNamesChange}
              // Find Time — only passed in self-schedule modes. The
              // schema renders a custom row below Duration that
              // mounts `<FindTimeField>` and hides itself until at
              // least one interviewer is selected (legacy parity).
              findTimeProps={
                isSelfSchedule
                  ? {
                      value: findTimeValue,
                      slotsCount: sharedState.selfScheduleSlots?.length ?? 0,
                      timezone: sharedState.timezone ?? '',
                      minimumNoticeHours,
                      availableDays,
                      onApply: handleFindTimeApply,
                      onClear: handleFindTimeClear,
                      onPreview: handlePreviewOpen,
                      onNoAvailability: handleFindTimeNoAvailability,
                    }
                  : undefined
              }
              bufferProps={
                isBufferEnabledByFlag
                  ? {
                      value: {
                        before: sharedState.bufferBeforeMinutes,
                        after: sharedState.bufferAfterMinutes,
                        enabled: sharedState.bufferEnabled,
                      },
                      onChange: handleBufferChange,
                    }
                  : undefined
              }
            />
          </Box>
          {isSelfSchedule ? (
            <SlotsPreviewModal
              isOpen={isPreviewOpen}
              onClose={handlePreviewClose}
              data={
                {
                  src: 'kula',
                  styleProps: {},
                  name: sharedState.interviewName,
                  duration: sharedState.duration,
                  timeZone: sharedState.timezone,
                  location_type: (formRef.current?.getValues?.()?.location ??
                    'google_meet') as string,
                  contact_name: candidateName,
                  slots: selfScheduleSlotsForPreview,
                } satisfies SlotsPreviewData
              }
            />
          ) : null}
        </Flex>
      );
    }
  )
);

DetailsStep.displayName = 'DetailsStep';
